import { Blanket, BlanketImage, BlanketInfo } from "@/components/core/blanket";
import { Button } from "@/components/core/buttons/button";
import { Form, FormField, FormGroup, FormItem, FormLabel, FormMessage } from "@/components/core/forms/form.tsx";
import { useModalControl } from "@/components/core/modals/use-modal-controller";
import { ScrollArea } from "@/components/core/scroller";
import Tooltip from "@/components/core/tooltip";
import { PdfViewer } from "@/components/pdf-viewer";
import type { CaseRecord } from "@/features/case-record/types/case-records";
import { queryClient } from "@/query-client";
import { zodResolver } from "@hookform/resolvers/zod";
import { IconEye, IconFile, IconPlus, IconTemplate, IconTrash, IconX } from "@tabler/icons-react";
import { type ChangeEvent, useEffect, useRef, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { useCaseActivityUpdateProofMutation } from "../api/queries/acitivities-query";
import { type CaseActivity, type CaseActivityForm, caseActivityProofFormSchema } from "../types/activities";

export default function CaseActivityProofModal({ caseRecord, data, showEditor = false }: { caseRecord: CaseRecord; data: CaseActivity; showEditor: boolean }) {
	const { closeModal } = useModalControl();
	const caseActivitiesMutation = useCaseActivityUpdateProofMutation(data?.id);
	const today = new Date();

	const [document, setDocument] = useState<string | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const localDate = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Manila" }));

	const form = useForm<CaseActivityForm>({
		resolver: zodResolver(caseActivityProofFormSchema),
		defaultValues: {
			id: data?.id,
			case_id: data?.case_id,
			attachment: null,
		},
	});

	const { watch } = form;
	const formatType = watch("format_type");
	const inputRef = useRef<HTMLInputElement | null>(null);
	const [multipleFiles, setMultipleFiles] = useState<File[]>([]);

	const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
	const [showAttachment, setShowAttachment] = useState(false);
	const attachmentUrl = form.watch("attachment_url") || data?.attachment;

	const handleRemoveAttachment = (index: number) => {
		setSelectedFiles((prevFiles) => {
			const updatedFiles = prevFiles.filter((_, i) => i !== index);
			form.setValue("attachment", updatedFiles); // Properly updating form state
			setMultipleFiles(updatedFiles);
			setShowAttachment(updatedFiles.length > 0);
			return updatedFiles; // Ensure state is properly updated
		});
	};

	const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
		const files = event.target.files;
		if (files && files.length > 0) {
			setSelectedFiles((prevFiles) => {
				const objectUrl = URL.createObjectURL(files[0]);
				setDocument(objectUrl);
				const newFiles = [...prevFiles, ...Array.from(files)];
				form.setValue("attachment", newFiles); // Updating form value with new file list
				setMultipleFiles(newFiles);
				setShowAttachment(true);
				return newFiles;
			});
		}
	};

	const submit = async (values) => {
		setIsSubmitting(true);
		try {
			await caseActivitiesMutation.mutateAsync(values);
			await queryClient.invalidateQueries({ queryKey: ["case_activities"] });
			await queryClient.invalidateQueries({ queryKey: ["case_records"] });

			setIsSubmitting(false);
			closeModal();
		} catch (error) {
			setIsSubmitting(false);
			console.log(error);
		}
	};

	return (
		<div className="flex flex-col w-full h-full">
			<Form {...form}>
				<form className="w-full h-full" onSubmit={form.handleSubmit(submit)}>
					<div className="flex flex-col w-full h-full">
						<div className="flex shrink-0 flex-row items-center justify-between w-full h-[50px] px-4 border-b border-slate-200">
							<span className="text-base font-bold text-foreground-dark">{data.activity} - Proof of Payment</span>
							<button
								onClick={() => {
									closeModal();
								}}
								className="flex w-[20px] h-[20px] ring-offset-background transition-opacity outline-none border-none ring-0 text-foreground opacity-40 hover:opacity-100 disabled:pointer-events-none"
								type="button"
							>
								<IconX size={20} stroke={1.5} />
							</button>
						</div>
						<PanelGroup className="flex flex-row grow w-full" direction="horizontal">
							<Panel defaultSize={30} minSize={30} maxSize={30} className="flex flex-col h-full min-w-[420px] max-w-[420px]">
								<ScrollArea className="h-[calc(100svh - 120px)]">
									<div className="grid grid-cols-12 w-full p-4 gap-x-3 gap-y-2 overflow-y-auto">
										<div className="col-span-12 flex w-full">
											<FormField
												control={form.control}
												name="attachment"
												render={() => (
													<FormItem>
														<div className="flex flex-col w-full">
															<FormLabel>Upload Files</FormLabel>
														</div>
														<FormGroup>
															<div className="flex flex-col w-full rounded-md border border-border">
																<div className="relative flex flex-row items-center w-full h-[38px] max-h-[38px] p-1.5">
																	<input
																		accept="application/pdf"
																		type="file"
																		ref={inputRef}
																		className="absolute inset-0 w-full h-full cursor-pointer opacity-0"
																		multiple
																		onChange={handleFileChange}
																	/>
																	<div className="flex flex-row items-center w-full gap-2">
																		<div className="inline-flex flex-row items-center px-3 h-[28px] gap-1 rounded bg-primary-600 font-semibold text-primary-foreground cursor-pointer">
																			<span className="text-sm">{multipleFiles.length > 0 ? "Add" : "Browse"}</span>
																		</div>
																		<span className="text-base text-foreground">
																			{multipleFiles.length === 1
																				? multipleFiles[0].name
																				: multipleFiles.length > 1
																					? "More than 2 files selected"
																					: "No file selected"}
																		</span>
																	</div>
																</div>
																{showAttachment && multipleFiles.length > 0 && (
																	<div className="flex flex-col w-full p-1.5 gap-2 border-t border-border">
																		{multipleFiles.map((file, index) => (
																			<div key={index} className="flex flex-row justify-between items-center">
																				<div className="items-center flex flex-row gap-2">
																					<div className="inline-flex shrink-0 justify-center items-center w-[32px] h-[32px] rounded-md bg-slate-200 text-foreground overflow-hidden">
																						<IconFile size={18} stroke={2} />
																					</div>
																					<div className="inline-flex flex-col">
																						<span className="truncate max-w-[240px] text-sm font-semibold text-foreground-dark">{file.name}</span>
																						<span className="text-sm text-foreground/50">{(file.size / 1024).toFixed(2)} KB</span>
																					</div>
																				</div>

																				<div className="inlineflex flex-row w-auto">
																					<Tooltip className="!z-[999]" content="Preview">
																						<button
																							className="inline-flex justify-center items-center w-[28px] h-[28px] ml-auto rounded-md text-foreground/50 hover:text-foreground"
																							type="button"
																							color="red"
																							onClick={() => {
																								const objectUrl = URL.createObjectURL(file);
																								setDocument(objectUrl);
																							}}
																						>
																							<IconEye size={18} stroke={2} />
																						</button>
																					</Tooltip>
																					<Tooltip className="!z-[999]" content="Remove">
																						<button
																							className="inline-flex justify-center items-center w-[28px] h-[28px] ml-auto rounded-md text-danger-600 hover:text-danger-500"
																							type="button"
																							color="red"
																							onClick={() => handleRemoveAttachment(index)}
																						>
																							<IconTrash size={18} stroke={2} />
																						</button>
																					</Tooltip>
																				</div>
																			</div>
																		))}
																	</div>
																)}
															</div>
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>
									</div>
								</ScrollArea>
							</Panel>

							<PanelResizeHandle className="group flex justify-center items-center w-[12px] border-r border-l border-border/50 bg-white">
								<div className="inline-flex w-[4px] h-[40px] rounded-sm bg-foreground/50 group-hover:bg-foreground/90" />
							</PanelResizeHandle>

							<Panel defaultSize={70} minSize={70} maxSize={70} className="flex flex-col shrink grow w-full h-full bg-slate-100">
								{<PdfViewer file={document} />}
							</Panel>
						</PanelGroup>
						<div className="flex shrink-0 flex-row items-center justify-end w-full h-[65px] px-4 gap-3 border-t border-slate-200 bg-background">
							<Button
								variant="ghost"
								type="button"
								onClick={() => {
									closeModal();
								}}
							>
								Cancel
							</Button>
							<Button disabled={isSubmitting || (data.type === "JDR" && !showAttachment)} loading={isSubmitting}>
								Submit
							</Button>
						</div>
					</div>
				</form>
			</Form>
		</div>
	);
}
